<template>
  <div class="position-fair-pay-card-view">
    <EXPermissionDeniedState v-if="forbidden" />
    <PositionFairPayCard
      v-else
      :title="header.title"
      :code="header.code"
      :status="header.status"
      :subscriptions="subscriptions"
      :assignments="assignments"
      :as-of="asOf"
      :loading="loading"
      :error="loadError"
      @back="onBack"
      @retry="load"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import { useRoute } from "vue-router/composables";
import PositionFairPayCard from "@components/PositionFairPayCard/PositionFairPayCard.vue";
import EXPermissionDeniedState from "@components/EXPermissionDeniedState/EXPermissionDeniedState.vue";
import {
  mapCardAssignments,
  mapCardSubscriptions,
  mapPositionHeader,
} from "@components/PositionFairPayCard/mapPositionFairPayCard";
import { listSubscriptionsForPosition } from "@/api/positionPayRangeSubscriptionAPI";
import { getPositionAssignmentsByPositionId } from "@/api/positionAPI";
import { getPositionId } from "@/api/roleAPI";

const props = defineProps({
  positionId: { type: [String, Number], default: null },
});

const route = useRoute();
const { proxy } = getCurrentInstance() || {};

const resolvedId = computed(() => props.positionId || route.params.positionId || route.params.id);

const loading = ref(true);
const loadError = ref("");
const forbidden = ref(false);
const header = ref({ title: "", code: "", status: "active" });
const subscriptions = ref([]);
const assignments = ref([]);

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

// The list's as-of date is carried back so a subscription entered on a future
// date still renders the subscription card instead of "+ Subscribe".
const asOf = computed(() => (route.query.asOf ? String(route.query.asOf) : ""));

function onBack(listRoute) {
  proxy?.$router?.push(listRoute).catch(() => {});
}

async function load() {
  const accountId = getAccountId();
  const id = resolvedId.value;
  loading.value = true;
  loadError.value = "";
  forbidden.value = false;

  if (!accountId || !id) {
    loading.value = false;
    loadError.value = "Position not found";
    return;
  }

  try {
    const [positionResponse, subscriptionResponse, assignmentResponse] = await Promise.all([
      getPositionId(accountId, id),
      listSubscriptionsForPosition(id),
      getPositionAssignmentsByPositionId(accountId, id, { limit: 200 }).catch(() => ({ data: [] })),
    ]);
    const position = positionResponse?.data || {};
    header.value = mapPositionHeader(position);
    subscriptions.value = mapCardSubscriptions(subscriptionResponse?.data);
    const occupant = position.employee || position.assigned_employees?.[0] || null;
    assignments.value = mapCardAssignments(assignmentResponse?.data, occupant);
  } catch (err) {
    // ER-12840 — a denied user sees the permission state, never a toast.
    if (err?.response?.status === 403) {
      forbidden.value = true;
    } else {
      loadError.value = "Failed to load position";
      if (proxy?.$notifications?.error) {
        proxy.$notifications.error("Failed to load position");
      }
    }
  } finally {
    loading.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.position-fair-pay-card-view {
  width: 100%;
  padding: 8px 0 24px;
}
</style>
